'use client';

import { Box, Typography, Container } from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { useRouter } from 'next/navigation';
import HeroSection from './components/HeroSection';
import FeatureCard from './components/FeatureCard';
import ProjectCard from './components/ProjectCard';
import TipCard from './components/TipCard';
import WaveDivider from './components/WaveDivider';
import FadeIn from './components/FadeIn';
import Image from 'next/image';
import AnimatedGradientText from './components/AnimatedGradientText';
import ShimmerButton from './components/ShimmerButton';
import { projects } from './portfolio/projects';

export default function HomePage() {
  const router = useRouter();

  return (
    <>
        <HeroSection />
        <FadeIn>
          <WaveDivider color="rgba(0,0,0,0.6)" flip={true} />
        </FadeIn>
        <Box sx={{ py: 4 }}>
        <Container maxWidth="lg">
          <FadeIn>
            <AnimatedGradientText variant="h2" fontWeight="bold" gutterBottom sx={{ textAlign: 'center'}}>
              Built to Automate the Grind
            </AnimatedGradientText>
          </FadeIn>
          <FadeIn>
            <Typography variant="body1" textAlign="center" sx={{ mb: 4 }}>
              From lead scraping to client onboarding, we create automations that replace repetitive tasks and give you time back.
            </Typography>
          </FadeIn>
          <Grid container spacing={4} sx={{ mb: 4 }}>
            {[
              { title: 'Lead Gen Scrapers', desc: 'Pull fresh leads from Google Maps and niche directories, filtered by industry and zip code.' },
              { title: 'Custom Bots', desc: 'Auto-book jobs, send alerts, or manage workflows. If it’s repetitive, we’ll automate it.' },
              { title: 'Chatbots & AI Assistants', desc: 'Convert visitors into leads with trained chatbots that learn your site and services.' },
              { title: 'CRM + Email Workflows', desc: 'Automate follow-ups, tagging, and list cleaning across CRMs like Mailchimp, Pipedrive, and HubSpot.' },
              { title: 'Document Automation', desc: 'Bulk upload, route, sign, and store docs via DocuSign or custom tools.' },
              { title: 'AI-Assisted Code Cleanup', desc: 'We fix broken AI-generated code, host it, and scale it into real products.' },
            ].map(({ title, desc }) => (
              <Grid item key={title} xs={12} sm={6} md={4}>
                <FeatureCard title={title} desc={desc} />
              </Grid>
            ))}
          </Grid>
          <FadeIn>
            <Box sx={{ py: 4, bgcolor: 'rgba(36,61,99,0.8)' }}>
              <Container maxWidth="lg">
                <AnimatedGradientText variant="h2" fontWeight="bold" gutterBottom sx={{ mb: 4, textAlign: 'center' }}>
                  Portfolio Highlights
                </AnimatedGradientText>
                <Grid container spacing={4} sx={{ mb: 4 }}>
                  {projects.slice(0, 3).map(({ slug, title, description }) => (
                    <Grid item xs={12} md={4} key={slug}>
                      <ProjectCard title={title} desc={description.split('\n')[0]} />
                    </Grid>
                  ))}
                </Grid>
                <Box textAlign="center" sx={{ mt: 2 }}>
                  <ShimmerButton variant="contained" onClick={() => router.push('/portfolio')}>
                    View Full Portfolio
                  </ShimmerButton>
                </Box>
              </Container>
            </Box>
          </FadeIn>
          <FadeIn>
            <Box sx={{ py: 4, bgcolor: 'rgba(36,61,99,0.8)' }}>
              <Container maxWidth="md">
              <AnimatedGradientText variant="h2" fontWeight="bold" gutterBottom sx={{ mb: 4 }}>
                What Clients Say
              </AnimatedGradientText>
              <Box sx={{ mb: 4 }}>
                <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 1 }}>
                  “Matt delivered an exceptional experience from start to finish! His attention to detail and exceptional communication ensured a smooth transition and a robust website on a secure host.”
                </Typography>
                <Typography variant="subtitle2" sx={{ mt: 1 }}>— Chuck, DandaLaw.com</Typography>
              </Box>
              <Box sx={{ mb: 4 }}>
                <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 1 }}>
                  “Awesome work. Exactly what I was looking for. Will work with again!”
                </Typography>
                <Typography variant="subtitle2">— Mike, Lead Scrubbing App</Typography>
              </Box>
            </Container>
          </Box>
          </FadeIn>
          <FadeIn>
          <Box sx={{ py: 4, bgcolor: 'rgba(13,17,23,0.7)', color: '#fff' }}>
            <Container maxWidth="md">
              <AnimatedGradientText variant="h2" fontWeight="bold" gutterBottom>
                Built in Florida, Serving Everyone
              </AnimatedGradientText>
              <Typography variant="body1" sx={{ mb: 4 }}>
                We know local business pain points because we’ve lived them. From Tampa to Tallahassee, we build tools to make your work less work.
              </Typography>
              <ShimmerButton variant="contained" size="large" onClick={() => router.push('/contact')}>
                Let’s Build Something Together
              </ShimmerButton>
            </Container>
          </Box>
          </FadeIn>
          <FadeIn>
          <Box sx={{ py: 4, bgcolor: 'rgba(36,61,99,0.8)' }}>
            <Container maxWidth="lg">
              <AnimatedGradientText variant="h2" fontWeight="bold" gutterBottom sx={{ mb: 4 }}>
                Tips & Tricks
              </AnimatedGradientText>
              <Grid container spacing={4} sx={{ mb: 4 }}>
                {[
                  {
                    slug: 'buy-a-google-maps-lead-scraper-and-never-pay-for-leads-again',
                    title: 'Buy a Google Maps Lead Scraper and Never Pay for Leads Again',
                    summary:
                      'A one-time purchase tool that scrapes business data—emails, phones, sites—filtered by keyword and zip code.',
                  },
                  {
                    slug: 'lead-generation-that-actually-works-from-custom-lists-to-full-funnel-automation',
                    title: 'Lead Gen That Actually Works',
                    summary:
                      'A full growth engine built around custom scrapers, automated outreach, CRM sync, and follow-ups.',
                  },
                ].map(({ slug, title, summary }) => (
                  <Grid item key={slug} xs={12} md={6} >
                    <TipCard title={title} summary={summary} slug={slug} />
                  </Grid>
                ))}
              </Grid>
              <Box textAlign="center" sx={{ mt: 2 }}>
                <ShimmerButton variant="contained" onClick={() => router.push('/blog')}>
                  View All Posts
                </ShimmerButton>
              </Box>
            </Container>
          </Box>
          </FadeIn>
          <WaveDivider color="rgba(0,0,0,0.6)" flip />
          </Container>
          </Box>
    </>
  );
}
